import { useNavigate } from 'react-router-dom';
import { memo, useMemo } from 'react';
import { usePages } from '@/features/pages/context/PagesContext';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import { useCollections } from '../../context/CollectionsContext';
import { useCollectionStore } from '../../stores/useCollectionStore';
import NestedListContainer from './NestedListContainer';
import AddSection from './AddSection';

export interface CollectionPageListProps {
  isPublicView?: boolean;
}

const CollectionPageList = ({ isPublicView = false }: CollectionPageListProps) => {
  const navigate = useNavigate();
  const currentPageSection = usePageStore(state => state.currentPage?.sectionId || '');
  const currentCollection = useCollectionStore(state => state.currentCollection);
  const collectionStructure = useCollectionStore(state => state.collectionStructure);
  const loading = useCollectionStore(state => state.loading);
  const editable = usePageStore(state => state.editable);
  const { addPage, archivePage } = usePages();
  const { updateStructureItem } = useCollections();

  const key = useMemo(
    () =>
      collectionStructure.length > 0
        ? collectionStructure
            .map(
              s =>
                s.section.pages.map(p => `${p.title}_${String(p.updatedAt)}`).join('') +
                s.section.title +
                String(s.section.orderNumber)
            )
            .join('')
        : '',
    [collectionStructure]
  );

  // onRenameSection: (section: Section) => void;
  const onAddSectionPage = async (sectionId: string, orderNumber: number) => {
    const collectionId = currentCollection?.id as string;
    const pageData = { sectionId, collectionId: collectionId, orderNumber };
    const newPageId = await addPage(pageData);
    if (newPageId) {
      navigate(`/collection/${collectionId}/page/${newPageId}`);
    }
  };
  const onArchiveSection = async (sectionId: string) => {
    const archivedSection = collectionStructure.find(s => s.section.id === sectionId);
    const newStructure = collectionStructure.filter(s => s.section.id !== sectionId);
    if (archivedSection?.section.pages.length) {
      await Promise.all(archivedSection.section.pages.map(p => archivePage(p.id)));
    }
    await updateStructureItem(newStructure);
    if (currentCollection?.id && currentPageSection === sectionId) {
      navigate(`/collection/${currentCollection.id}`);
    }
  };

  if (collectionStructure.length === 0 && !loading) {
    return null;
  }

  return (
    <div
      className="flex flex-col items-start space-y-1 relative overflow-x-hidden pb-4"
      style={{
        maxHeight: isPublicView ? 'calc(100vh - 100px)' : 'calc(100vh - 160px)',
        overflowY: 'auto',
      }}
    >
      {!isPublicView && !currentCollection?.archivedAt && collectionStructure.length === 0 && (
        <span className="text-sm text-muted-foreground px-4 pt-3">Add more notes here</span>
      )}
      <NestedListContainer
        key={key}
        displayedSections={collectionStructure}
        onUpdate={updateStructureItem}
        onAddSectionPage={onAddSectionPage}
        onArchiveSection={onArchiveSection}
        disabled={isPublicView || !!currentCollection?.archivedAt || !editable}
      />
      {!currentCollection?.archivedAt && !isPublicView && <AddSection />}
    </div>
  );
};

export default memo(CollectionPageList);
